<template>
  <section ref="screenRoot" class="page project-screen" data-test="project-screen">
    <!-- Hero -->
    <div class="project-hero">
      <div class="anim" data-delay="80">
        <Eyebrow pulse>Project · {{ projectKey }}</Eyebrow>
        <DisplayHeading :lead="projectKey" accent="overview" />
        <p class="display-sub" data-test="portfolio-sub">
          {{ boards.length }} board{{ boards.length === 1 ? "" : "s" }}
        </p>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorObj" class="error-state anim" data-delay="180">
      <p class="error-title">Couldn't load this project</p>
      <p class="error-msg">{{ errorObj.message }}</p>
      <button class="retry" type="button" @click="store.load(true)">Retry</button>
    </div>

    <!-- Loading -->
    <div v-else-if="isInitialLoading" class="skeletons anim" data-delay="200" data-test="loading">
      <div class="skel-row big" />
      <div class="skel-row" />
    </div>

    <!-- Empty -->
    <div v-else-if="boards.length === 0" class="empty anim" data-delay="200" data-test="portfolio-empty">
      No boards recorded for {{ projectKey }} yet.
    </div>

    <!-- Board portfolio -->
    <div v-else class="board-grid anim" data-delay="220" data-test="portfolio-grid">
      <component
        :is="b.board_id != null ? 'RouterLink' : 'div'"
        v-for="b in boards"
        :key="b.rowId"
        :to="b.board_id != null ? boardTo(b.board_id) : undefined"
        class="board-card"
        :class="{ 'is-link': b.board_id != null }"
        :data-test="`portfolio-board-${b.board_id ?? b.boardType}`"
      >
        <div class="bc-head">
          <span class="bc-name">{{ b.boardName }}</span>
          <BoardTypeBadge :board-type="b.boardType" />
        </div>
        <p class="bc-latest">
          <span v-if="b.last" class="bc-sprint">{{ b.last.sprint_name }}</span>
          <span v-else class="bc-sprint muted">No sprints yet</span>
          <RiskPill v-if="b.band === 'red'" variant="high">At risk</RiskPill>
          <RiskPill v-else-if="b.band === 'amber'" variant="med">Watch</RiskPill>
          <RiskPill v-else-if="b.band === 'green'" variant="low">On track</RiskPill>
        </p>
        <p class="bc-meta">
          {{ b.sprint_count }} {{ b.boardType === 'kanban' ? 'window snapshot' : 'sprint' }}{{ b.sprint_count === 1 ? '' : 's' }}
          <span v-if="b.last?.sprint_state === 'active'" class="bc-live">· live</span>
        </p>
        <span v-if="b.board_id != null" class="bc-open">Open scorecard →</span>
      </component>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import BoardTypeBadge from "@/components/BoardTypeBadge.vue";
import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import Eyebrow from "@/components/scorecard/Eyebrow.vue";
import RiskPill from "@/components/scorecard/RiskPill.vue";
import { useReveal } from "@/composables/useReveal";
import { useProjectsStore } from "@/stores/projects";
import type { ProjectListItem, RagBand } from "@/api/types";

const route = useRoute();
const router = useRouter();
const store = useProjectsStore();
const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);

const projectKey = computed(() => String(route.params.key ?? ""));
const errorObj = computed(() => store.error);
const isInitialLoading = computed(() => store.loading && store.items.length === 0);

// Cheap health band from the last sprint's signals — no full sprint fetch.
function bandFor(p: ProjectListItem): RagBand {
  const ls = p.last_sprint;
  if (!ls) return "none";
  if (ls.low_signal) return "red";
  if (ls.m18_start_readiness === false) return "amber";
  if (ls.coverage?.pct != null && ls.coverage.pct < 0.5) return "amber";
  return "green";
}

// Board ids whose sprints span more than one project key. Opening one "raw"
// lands on the per-project breakdown grid, so links to a shared board must
// carry ?project=KEY to reach this project's focused scorecard. (The /projects
// feed already excludes the `__board__` config row, so this counts only real
// project keys.)
const sharedBoardIds = computed<Set<number>>(() => {
  const byBoard = new Map<number, Set<string>>();
  for (const p of store.items) {
    if (p.board_id == null) continue;
    const keys = byBoard.get(p.board_id) ?? new Set<string>();
    keys.add(p.key);
    byBoard.set(p.board_id, keys);
  }
  const shared = new Set<number>();
  for (const [bid, keys] of byBoard) if (keys.size > 1) shared.add(bid);
  return shared;
});

function boardTo(boardId: number) {
  return sharedBoardIds.value.has(boardId)
    ? { name: "board", params: { boardId: String(boardId) }, query: { project: projectKey.value } }
    : { name: "board", params: { boardId: String(boardId) } };
}

const boards = computed(() =>
  store.items
    .filter((p) => p.key === projectKey.value)
    .map((p) => {
      const boardType = (p.board_type ?? "scrum") === "kanban" ? "kanban" : "scrum";
      return {
        rowId: p.board_id != null ? `b${p.board_id}` : `type-${boardType}`,
        board_id: p.board_id ?? null,
        boardName: p.board_name || (p.board_id != null ? `Board ${p.board_id}` : projectKey.value),
        boardType: boardType as "scrum" | "kanban",
        sprint_count: p.sprint_count,
        last: p.last_sprint ?? null,
        band: bandFor(p),
      };
    })
    // Scrum boards first, then by name for stable ordering.
    .sort((a, b) => (a.boardType === b.boardType ? a.boardName.localeCompare(b.boardName) : a.boardType === "scrum" ? -1 : 1)),
);

// A project with exactly one board has no portfolio decision to make — send
// the user straight to that board's scorecard. When that single board is
// SHARED (its sprints span multiple projects), the bare board route would only
// render the per-project breakdown grid — which links right back here, an
// infinite bounce. So scope the redirect to ?project=KEY, which opens the
// board focused on this project's scorecard instead.
function maybeRedirectSingleBoard(): void {
  if (store.loading || store.error) return;
  if (boards.value.length === 1 && boards.value[0].board_id != null) {
    void router.replace(boardTo(boards.value[0].board_id));
  }
}

onMounted(() => { void store.load(); });
watch(
  [() => store.loading, () => boards.value.length],
  () => maybeRedirectSingleBoard(),
  { immediate: true },
);
</script>

<style scoped>
.project-screen { position: relative; }
.project-hero { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 36px; }

.error-state { padding: 16px; background: var(--red-soft); border: 1px solid color-mix(in srgb, var(--red) 30%, transparent); border-radius: 12px; color: var(--red-ink); display: grid; gap: 8px; }
.error-title { font-weight: 500; margin: 0; }
.error-msg { font-size: 13px; margin: 0; color: var(--ink-2); }
.retry { align-self: start; background: var(--bg-elev); border: 1px solid color-mix(in srgb, var(--red) 30%, transparent); padding: 6px 14px; border-radius: 8px; cursor: pointer; color: var(--red-ink); }

.skeletons { display: grid; gap: 12px; }
.skel-row { height: 96px; border-radius: 12px; background: var(--skeleton); }
.skel-row.big { height: 120px; }
.empty { padding: 48px; border: 1px dashed var(--line); border-radius: 12px; color: var(--muted); text-align: center; }

.board-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
.board-card {
  display: grid; gap: 8px; align-content: start;
  padding: 20px; border: 1px solid var(--line); border-radius: 14px;
  background: var(--bg-elev); text-decoration: none; color: inherit;
}
.board-card.is-link { transition: border-color var(--speed-quick) var(--ease), transform var(--speed-quick) var(--ease); }
.board-card.is-link:hover { border-color: var(--ink); transform: translateY(-2px); }
.bc-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.bc-name { font-size: 17px; font-weight: 600; color: var(--ink); letter-spacing: -0.02em; }
.bc-latest { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 0; font-size: 13px; color: var(--ink-2); }
.bc-sprint.muted { color: var(--muted); }
.bc-meta { margin: 0; font-size: 12px; color: var(--muted); }
.bc-live { color: var(--green); font-weight: 600; }
.bc-open { font-size: 13px; color: var(--brand); font-weight: 500; margin-top: 2px; }
</style>
